import React from 'react';

import { useSelector } from 'hooks';
import { Route, Switch } from 'react-router-dom';

import ProtectedRoute from 'components/shared/ProtectedRoute';

import type { TabType } from '../types';

interface TabsRouterProp {
  page: string;
  views: TabType[];
  requiredGrants: string;
  /** The view the tab bar and toolbar show, rendered when no route matches. */
  fallbackView?: TabType;
}

const TabsRouter: React.FC<TabsRouterProp> = ({
  page,
  views,
  requiredGrants,
  fallbackView
}) => {
  'use memo';

  const grants = useSelector((state) => state.grants);

  const FallbackComponent = fallbackView?.Component;

  return (
    <ProtectedRoute isAccessGranted={grants[requiredGrants]} redirectPath={'/'}>
      {/* `Switch` renders the first matching route only, which the mutually
          exclusive `page=` paths below already guaranteed - what it buys us is the
          catch-all at the end. */}
      <Switch>
        {views.map((tableView) =>
          tableView.Component ? (
            <Route
              key={tableView.name}
              path={`/${page}&page=${tableView.name}`}
            >
              <tableView.Component />
            </Route>
          ) : null
        )}
        {/* A page param that resolves to nothing - a view behind a grant this user
            does not have - used to leave the content area empty next to a filled
            tab bar. `useTabNavigation` already fell back to a view it can reach, so
            render that one instead of nothing. */}
        {FallbackComponent && (
          <Route path="*">
            <FallbackComponent />
          </Route>
        )}
      </Switch>
    </ProtectedRoute>
  );
};

export default TabsRouter;
